/**
 * AudioBloom Visualizer - Circular audio spectrum with particle bursts
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata } from '../types';

export class AudioBloomVisualizer extends BaseVisualizer {

  readonly metadata: VisualizerMetadata = {
    id: 'audiobloom',
    name: 'Audio Bloom',
    description: 'Circular audio spectrum with energy particles and beat detection',
    category: 'artistic',
    defaultZoom: 1.0,
    supports3D: false,
    supportsAutoRotation: false,
    supportsPanning: false,
    supportsZooming: true,
  };

  render(config: VisualizerConfig): void {
    const {
      ctx, width, height, activeWaves, speed, time, zoom, uploadedAudioData, isUploadedAudioPlaying
    } = config;
    const cx = width / 2;
    const cy = height / 2;

    const useUploadedAudio = Boolean(isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0);
    const displayWaves = activeWaves;

    // Deep space background
    const bgGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(width, height) * 0.8);
    bgGradient.addColorStop(0, '#0a0520');
    bgGradient.addColorStop(0.7, '#050212');
    bgGradient.addColorStop(1, '#000000');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, width, height);

    const baseRadius = Math.min(width, height) * 0.15 * zoom;
    const maxRadius = Math.min(width, height) * 0.45 * zoom;
    const numBands = 64;

    // === BUILD SPECTRUM ===
    const spectrum: number[] = [];

    if (useUploadedAudio && uploadedAudioData) {
      // Use full music spectrum with logarithmic bands (20 Hz - 20 kHz) to preserve musical spacing
      const minFreq = 20;
      const maxFreq = 20000;
      const logMin = Math.log(minFreq);
      const logMax = Math.log(maxFreq);

      for (let i = 0; i < numBands; i++) {
        const bandFreq = Math.exp(logMin + (i / numBands) * (logMax - logMin));
        const nextBandFreq = Math.exp(logMin + ((i + 1) / numBands) * (logMax - logMin));

        let bandEnergy = 0;

        // uploadedAudioData is interleaved [freq, amp, channel]
        for (let j = 0; j + 2 < uploadedAudioData.length; j += 3) {
          const audioFreq = uploadedAudioData[j];
          const audioAmp = uploadedAudioData[j + 1];

          if (audioFreq >= bandFreq && audioFreq < nextBandFreq) {
            bandEnergy += audioAmp * 100; // align with legacy scaling
          }
        }

        spectrum.push(bandEnergy);
      }
    } else {
      // Fallback for synthesized waves: normalized 0-100 range
      for (let i = 0; i < numBands; i++) {
        const freqRange = (i / numBands) * 100;
        const bandWidth = 100 / numBands;
        let bandEnergy = 0;

        displayWaves.forEach(wave => {
          const effectiveFreq = wave.freq * speed;
          if (effectiveFreq >= freqRange && effectiveFreq < freqRange + bandWidth) {
            const phase = (wave.phase * Math.PI / 180) + time * effectiveFreq;
            const amplitude = wave.amp * Math.abs(Math.sin(phase)) * 2;
            bandEnergy += amplitude;
          } else {
            const distance = Math.abs(effectiveFreq - freqRange);
            if (distance < bandWidth * 4) {
              const falloff = 1 - (distance / (bandWidth * 4));
              const phase = (wave.phase * Math.PI / 180) + time * effectiveFreq;
              bandEnergy += wave.amp * Math.abs(Math.sin(phase)) * falloff * 0.8;
            }
          }
        });

        spectrum.push(bandEnergy);
      }
    }

    // Smooth spectrum
    const smoothedSpectrum = spectrum.map((val, i) => {
      if (i === 0 || i === spectrum.length - 1) return val;
      return (spectrum[i - 1] + val * 2 + spectrum[i + 1]) / 4;
    });

    // Calculate energy levels
    const totalEnergy = smoothedSpectrum.reduce((sum, val) => sum + val, 0);
    const avgEnergy = totalEnergy / numBands;
    const bassEnergy = smoothedSpectrum.slice(0, Math.floor(numBands * 0.2)).reduce((sum, val) => sum + val, 0);
    const midEnergy = smoothedSpectrum.slice(Math.floor(numBands * 0.2), Math.floor(numBands * 0.6)).reduce((sum, val) => sum + val, 0);
    const trebleEnergy = smoothedSpectrum.slice(Math.floor(numBands * 0.6)).reduce((sum, val) => sum + val, 0);
    
    // Beat detection
    const beatThreshold = avgEnergy * 1.5;
    const isBeat = bassEnergy > beatThreshold;

    // === BASS PULSE CORE ===
    const corePulse = isBeat ? 1.5 : 1 + (bassEnergy / 100) * 0.3;
    const coreRadius = baseRadius * 0.4 * corePulse;

    ctx.shadowBlur = isBeat ? 40 : 20;
    ctx.shadowColor = `rgba(255, ${Math.floor(100 + bassEnergy)}, 150, 0.8)`;

    const coreGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreRadius);
    coreGradient.addColorStop(0, `rgba(255, 255, 255, ${0.9 * corePulse})`);
    coreGradient.addColorStop(0.4, `rgba(255, ${Math.floor(150 + bassEnergy)}, 200, ${0.7 * corePulse})`);
    coreGradient.addColorStop(0.8, `rgba(200, 100, 255, ${0.4 * corePulse})`);
    coreGradient.addColorStop(1, 'rgba(100, 50, 200, 0)');

    ctx.fillStyle = coreGradient;
    ctx.beginPath();
    ctx.arc(cx, cy, coreRadius, 0, Math.PI * 2);
    ctx.fill();

    // === CIRCULAR SPECTRUM BARS ===
    smoothedSpectrum.forEach((energy, i) => {
      const angle = (i / numBands) * Math.PI * 2 - Math.PI / 2;
      const normalizedEnergy = Math.min(1, energy / 100);

      // Color based on frequency
      const freqRatio = i / numBands;
      let r, g, b;
      if (freqRatio < 0.33) {
        r = 255;
        g = Math.floor(freqRatio * 3 * 180);
        b = 50;
      } else if (freqRatio < 0.67) {
        r = Math.floor((1 - (freqRatio - 0.33) * 3) * 180);
        g = 255;
        b = Math.floor((freqRatio - 0.33) * 3 * 200);
      } else {
        r = Math.floor((freqRatio - 0.67) * 3 * 200);
        g = 50;
        b = 255;
      }

      const barHeight = normalizedEnergy * (maxRadius - baseRadius);
      const barInnerRadius = baseRadius;
      const barOuterRadius = baseRadius + barHeight;

      // Draw bar
      const barWidth = (Math.PI * 2 / numBands) * 0.8;

      ctx.shadowBlur = 10 + normalizedEnergy * 20;
      ctx.shadowColor = `rgba(${r}, ${g}, ${b}, ${0.6 + normalizedEnergy * 0.4})`;

      const gradient = ctx.createLinearGradient(
        cx + Math.cos(angle) * barInnerRadius,
        cy + Math.sin(angle) * barInnerRadius,
        cx + Math.cos(angle) * barOuterRadius,
        cy + Math.sin(angle) * barOuterRadius
      );
      gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.3)`);
      gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${0.6 + normalizedEnergy * 0.3})`);
      gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, ${0.8 + normalizedEnergy * 0.2})`);

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(cx, cy, barOuterRadius, angle - barWidth / 2, angle + barWidth / 2);
      ctx.arc(cx, cy, barInnerRadius, angle + barWidth / 2, angle - barWidth / 2, true);
      ctx.closePath();
      ctx.fill();

      // Bright tip
      if (normalizedEnergy > 0.3) {
        const tipX = cx + Math.cos(angle) * barOuterRadius;
        const tipY = cy + Math.sin(angle) * barOuterRadius;

        ctx.fillStyle = `rgba(255, 255, 255, ${normalizedEnergy * 0.8})`;
        ctx.beginPath();
        ctx.arc(tipX, tipY, 2 + normalizedEnergy * 2, 0, Math.PI * 2);
        ctx.fill();
      }
    });

    // === WAVEFORM RING ===
    const waveRingRadius = maxRadius * 1.2;
    const wavePoints = 120;

    ctx.strokeStyle = `rgba(100, 200, 255, 0.5)`;
    ctx.lineWidth = 2;
    ctx.shadowBlur = 15;
    ctx.shadowColor = 'rgba(100, 200, 255, 0.6)';

    ctx.beginPath();
    for (let i = 0; i <= wavePoints; i++) {
      const t = (i / wavePoints) * Math.PI * 2;

      // Calculate waveform at this angle
      let waveValue = 0;
      displayWaves.forEach(wave => {
        const effectiveFreq = wave.freq * speed;
        const phase = (wave.phase * Math.PI / 180) + time * effectiveFreq;
        waveValue += wave.amp * Math.sin(phase + t * 3) / 100;
      });

      const radius = waveRingRadius + waveValue * 30;
      const x = cx + Math.cos(t) * radius;
      const y = cy + Math.sin(t) * radius;

      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    // === PARTICLE BURST SYSTEM ===
    const particlesPerBand = 3;

    smoothedSpectrum.forEach((energy, i) => {
      if (energy < 10) return;

      const angle = (i / numBands) * Math.PI * 2 - Math.PI / 2;
      const normalizedEnergy = Math.min(1, energy / 100);
      const barRadius = baseRadius + normalizedEnergy * (maxRadius - baseRadius);

      // Color based on frequency
      const freqRatio = i / numBands;
      let r, g, b;
      if (freqRatio < 0.33) {
        r = 255; g = Math.floor(freqRatio * 3 * 180); b = 50;
      } else if (freqRatio < 0.67) {
        r = Math.floor((1 - (freqRatio - 0.33) * 3) * 180); g = 255; b = Math.floor((freqRatio - 0.33) * 3 * 200);
      } else {
        r = Math.floor((freqRatio - 0.67) * 3 * 200); g = 50; b = 255;
      }

      for (let p = 0; p < particlesPerBand; p++) {
        const particlePhase = (time * 2 + i * 0.3 + p * 0.5) % 1;
        const particleDistance = barRadius + particlePhase * (maxRadius - barRadius) * 0.6;

        const spreadAngle = angle + (Math.sin(time * 3 + p) * 0.15);
        const px = cx + Math.cos(spreadAngle) * particleDistance;
        const py = cy + Math.sin(spreadAngle) * particleDistance;

        const particleSize = (1 - particlePhase) * normalizedEnergy * 4 + 1;
        const particleAlpha = (1 - particlePhase) * normalizedEnergy;

        ctx.shadowBlur = 8;
        ctx.shadowColor = `rgba(${r}, ${g}, ${b}, ${particleAlpha * 0.8})`;
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${particleAlpha})`;
        ctx.beginPath();
        ctx.arc(px, py, particleSize, 0, Math.PI * 2);
        ctx.fill();
      }
    });

    // === BEAT RINGS ===
    if (isBeat) {
      const beatPhase = (time * 3) % 1;
      const beatRadius = baseRadius + beatPhase * (maxRadius - baseRadius) * 1.5;
      const beatAlpha = (1 - beatPhase) * 0.6;

      ctx.strokeStyle = `rgba(255, 100, 200, ${beatAlpha})`;
      ctx.lineWidth = 4 * (1 - beatPhase);
      ctx.shadowBlur = 20;
      ctx.shadowColor = `rgba(255, 100, 200, ${beatAlpha})`;
      ctx.beginPath();
      ctx.arc(cx, cy, beatRadius, 0, Math.PI * 2);
      ctx.stroke();

      // Secondary ring
      const beat2Radius = baseRadius + (beatPhase * 0.7) * (maxRadius - baseRadius) * 1.5;
      const beat2Alpha = (1 - beatPhase * 0.7) * 0.4;
      ctx.strokeStyle = `rgba(150, 100, 255, ${beat2Alpha})`;
      ctx.lineWidth = 3 * (1 - beatPhase);
      ctx.beginPath();
      ctx.arc(cx, cy, beat2Radius, 0, Math.PI * 2);
      ctx.stroke();
    }

    // === ENERGY LEVEL INDICATORS (top-left) ===
    const indicatorY = 70;
    const indicatorWidth = 60;
    const indicatorHeight = 8;
    const spacing = 10;

    const bandCounts = {
      bass: Math.max(1, Math.floor(numBands * 0.2)),
      mid: Math.max(1, Math.floor(numBands * 0.4)),
      treble: Math.max(1, numBands - Math.floor(numBands * 0.6)),
    };

    // Normalize energy by average-per-band to keep indicators from pegging
    const normalizeBand = (bandEnergy: number, bands: number) => {
      const bandAvg = bandEnergy / bands;
      const globalAvg = avgEnergy || 1;
      const norm = bandAvg / (globalAvg * 2); // give ~2x headroom before clipping
      return Math.min(1, Math.max(0, norm)) * indicatorWidth;
    };

    ctx.shadowBlur = 0;

    // Bass
    ctx.fillStyle = 'rgba(255, 100, 100, 0.3)';
    ctx.fillRect(20, indicatorY, indicatorWidth, indicatorHeight);
    ctx.fillStyle = 'rgba(255, 100, 100, 0.8)';
    ctx.fillRect(20, indicatorY, normalizeBand(bassEnergy, bandCounts.bass), indicatorHeight);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.font = '10px sans-serif';
    ctx.fillText('BASS', 20, indicatorY - 4);

    // Mid
    const midY = indicatorY + indicatorHeight + spacing;
    ctx.fillStyle = 'rgba(100, 255, 100, 0.3)';
    ctx.fillRect(20, midY, indicatorWidth, indicatorHeight);
    ctx.fillStyle = 'rgba(100, 255, 100, 0.8)';
    ctx.fillRect(20, midY, normalizeBand(midEnergy, bandCounts.mid), indicatorHeight);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.fillText('MID', 20, midY - 4);

    // Treble
    const trebleY = indicatorY + (indicatorHeight + spacing) * 2;
    ctx.fillStyle = 'rgba(100, 100, 255, 0.3)';
    ctx.fillRect(20, trebleY, indicatorWidth, indicatorHeight);
    ctx.fillStyle = 'rgba(100, 100, 255, 0.8)';
    ctx.fillRect(20, trebleY, normalizeBand(trebleEnergy, bandCounts.treble), indicatorHeight);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.fillText('TREBLE', 20, trebleY - 4);

    ctx.shadowBlur = 0;
  }
}
